import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { Translate } from "app/filters/i18nFilter";
import { UNKNOWN_COUNTRY_ID, WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import { countriesMap } from "./country-map";

export interface ICountryObject<T = number> {
    code: string;
    alpha2: string;
    alpha3: string;
    id: T;
    icon?: string;
    text: string;
    states?: ICountryObject[];
    children?: ICountryObject[];
    parent?: ICountryObject;
    mobileWeb?: boolean;
    permitted?: boolean;
    showDeviceIcon?: boolean;
    data?: number;
    key?: number;
    isLastPermitted?: boolean;
    tooltipText?: string;
    isClickAllowed?: boolean;
}

export interface ICountryService {
    countries: ICountryObject[];
}

export type SimilarwebCountryConfig = {
    countries?: unknown[];
};

export const getSimilarwebCountryConfig = (): SimilarwebCountryConfig => {
    if (typeof window === "undefined") {
        return {};
    }
    const top = window as unknown as Record<string, unknown>;
    const similarweb = top["similarweb"];
    if (!similarweb || typeof similarweb !== "object") {
        return {};
    }
    const config = (similarweb as Record<string, unknown>)["config"];
    if (!config || typeof config !== "object") {
        return {};
    }
    return config as SimilarwebCountryConfig;
};

const cloneCountriesInput = (source: unknown[]): ICountryObject[] =>
    source.map((raw) => {
        const c = raw as ICountryObject & { states?: ICountryObject[] };
        return {
            ...c,
            states: c.states ? c.states.map((s) => ({ ...s })) : c.states,
        };
    });

export const createCountryService = (countriesInput: unknown[], translateFn: Translate): ICountryService => {
    const countries = cloneCountriesInput(countriesInput);

    if (!countries.some(({ id }) => id === WORLDWIDE_COUNTRY_ID)) {
        countries.unshift({ id: WORLDWIDE_COUNTRY_ID, code: "ww" } as ICountryObject);
    }
    if (!countries.some(({ id }) => id === UNKNOWN_COUNTRY_ID)) {
        countries.unshift({ id: UNKNOWN_COUNTRY_ID, code: "xx" } as ICountryObject);
    }
    countries.forEach((country) => {
        country.children = [];
        country.icon = "flag flag-" + country.code;
        country.text =
            country.id === UNKNOWN_COUNTRY_ID
                ? translateFn("global.unknown")
                : translateFn("common.country." + country.id);
        if (country.states && country.states.length) {
            country.states.forEach((state) => {
                state.text = translateFn("common.country." + state.id);
                country.children!.push(state);
            });
        }
        const countryMapEntry = countriesMap.find((c) => c.Id === country.id);
        country.alpha2 = countryMapEntry?.Alpha2Code ?? "";
        country.alpha3 = countryMapEntry?.Alpha3Code ?? "";
    });

    return { countries };
};

export const useCountries = (): ICountryService => {
    const { translate } = useTranslate();
    const config = getSimilarwebCountryConfig();

    return useMemo(
        () => createCountryService(config.countries ?? [], translate),
        // config object from window is referentially stable (same global), but properties are not
        // Use a stable signal like the array length or a JSON key
        [translate],
    );
};
